import { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import aiIconData from '@/assets/lottie/ai-icon.json';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { toggleChat, checkAIHealth } from '@/store/slices/aiSlice';
import { cn } from '@/utils/helpers';

interface AIChatButtonProps {
  className?: string;
}

export function AIChatButton({ className }: AIChatButtonProps) {
  const dispatch = useAppDispatch();
  const { isOpen, health } = useAppSelector((state) => state.ai);

  useEffect(() => {
    dispatch(checkAIHealth());
  }, [dispatch]);

  const isConnected = health?.ollama?.connected ?? false;
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // Play one loop on mount, then stop to save GPU/battery
  const handleComplete = useCallback(() => {
    lottieRef.current?.stop();
  }, []);

  // Hide button when chat is open
  if (isOpen) return null;

  return (
    <motion.button
      onClick={() => dispatch(toggleChat())}
      className={cn(
        'fixed z-50',
        'bottom-6 right-6 md:bottom-8 md:right-8',
        className
      )}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    >
      <div className="relative w-14 h-14 md:w-16 md:h-16">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="w-full h-full rounded-full overflow-hidden"
        >
          {/* Lottie bubble is ~57% of its 700x700 canvas, so render at ~175% to fill the container */}
          <div className="w-full h-full flex items-center justify-center">
            <Lottie
              lottieRef={lottieRef}
              animationData={aiIconData}
              loop={false}
              autoplay
              onComplete={handleComplete}
              style={{ width: '175%', height: '175%', minWidth: '175%', minHeight: '175%' }}
            />
          </div>
        </motion.div>

        {/* Connection status dot — bottom-right, snug against the bubble */}
        <div
          className={cn(
            'absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full',
            'ring-2 ring-white dark:ring-gray-900',
            'transition-colors duration-300',
            isConnected ? 'bg-green-500' : 'bg-red-500'
          )}
        />
      </div>
    </motion.button>
  );
}

export default AIChatButton;
